// index.tsx
//
// (c) NCR Voyix Corporation 2025. All rights reserved.

import React, { useEffect, useState } from 'react'
import { TextField } from '@mui/material'

export type TextBoxType = 'text' | 'password'

/**
 * Props for the {@link TextBox} component.
 */
export interface TextBoxProps {
  /** The name of the textbox field */
  label: string
  /** The visual style variant of the text box */
  variant: 'standard' | 'filled' | 'outlined'
  /**
   * Callback function called when the text box value changes
   *
   * @remarks This function is debounced by 300ms to prevent excessive calls during rapid input.
   * */
  onChange: (value: string) => void
  /** If true, the text box will take up the full width of its container */
  fullWidth?: boolean
  /** The initial value of the text box */
  initialValue?: string
  /** Maximum number of characters allowed in the text box */
  characterLimit?: number
  /** The type of input, e.g., 'text' or 'password' */
  type?: TextBoxType

  /** Helper text to display when there is an error. (Note: requires error property) */
  errorHelperText?: string

  /** If true, the text box will be displayed in an error state. (Note: requires errorHelperText property) */
  error?: boolean
}

/**
 * Renders a controlled text input field with a debounced `onChange` callback
 *
 * @example
 * ```tsx
 * <TextBox
 *   label="Password"
 *   variant="filled"
 *   onChange={(value) => setPassword(value)}
 *   fullWidth={true}
 *   initialValue=""
 *   characterLimit={20}
 *   type="password"
 *   error={hasError}
 *   errorHelperText="Password is required"
 * />
 * ```
 */
export function TextBox(
  {
    label,
    variant,
    onChange,
    fullWidth,
    initialValue,
    type = 'text',
    characterLimit = undefined,
    error = false,
    errorHelperText = undefined
  }: Readonly<TextBoxProps>
): JSX.Element {
  const [value, setValue] = useState(initialValue ?? '')
  if (error && errorHelperText == null) throw new Error('errorHelperText must be provided when error is true')

  useEffect(() => {
    const timeout = setTimeout(() => {
      onChange(value)
    }, 300)
    return () => clearTimeout(timeout)
  }, [value])

  const getHelperText = (length: number): string | undefined => {
    if (error) {
      return errorHelperText
    }

    if (characterLimit) {
      return `${length}/${characterLimit}`
    }

    return undefined
  }

  return (
    <TextField
      label={label}
      variant={variant}
      fullWidth={fullWidth}
      value={value}
      onChange={(e) => setValue(e.target.value)}
      type={type}
      inputProps={{
        maxLength: characterLimit
      }}
      helperText={getHelperText(value.length)}
      error={error}
    />
  )
}
